import React, { useDeferredValue, useState } from 'react';
import { useQueries, useQuery } from 'react-query';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'src/redux-file/hooks';
import { client } from 'src/utils/api-client';
import { getLcaApiErrorMessage } from 'src/Lca/apiErrors';
import {
  StyledDiagramContainer,
  StyledLabel,
} from '../MonteCarloDiagram/index.styles';
import {
  DEFAULT_SCENARIO_CONTROLS,
  MAX_SAVED_SCENARIOS,
  SavedScenario,
  SCENARIO_COMPARE_COLORS,
  ScenarioControls,
  ScenarioImpact,
  ScenarioSimulationResponse,
} from '../analytics';

interface ScenarioSimulationPanelProps {
  productId: number;
  controls: ScenarioControls;
  onControlsChange: (_controls: ScenarioControls) => void;
  savedScenarios: SavedScenario[];
  onSavedScenariosChange: (_scenarios: SavedScenario[]) => void;
}

interface ScenarioCardProps {
  title: string;
  description: string;
  sliderLabel: string;
  sliderValue: number;
  min: number;
  max: number;
  step: number;
  onSliderChange: (_value: number) => void;
  result?: ScenarioImpact;
  unitName?: string;
  helperText?: string;
  resultLabel: string;
}

interface ComparisonMetric {
  key: keyof Pick<
    ScenarioSimulationResponse,
    'recycled_content' | 'lightweighting' | 'transport_optimization'
  >;
  title: string;
  subtitle: string;
}

const formatNumber = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const getDeltaColor = (delta = 0) => (delta <= 0 ? '#2E7D32' : '#B3261E');

const createScenarioId = () =>
  `scenario-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const areControlsEqual = (first: ScenarioControls, second: ScenarioControls) =>
  first.targetRecycledSharePct === second.targetRecycledSharePct &&
  first.materialReductionPct === second.materialReductionPct &&
  first.transportReductionPct === second.transportReductionPct;

const fetchScenarioSimulation = (
  productId: number,
  areaId: number,
  scenarioControls: ScenarioControls
) =>
  client
    .get<ScenarioSimulationResponse>(
      `/web/lca/scenario_simulation/${productId}`,
      {
        params: {
          area_id: areaId,
          target_recycled_share_pct: scenarioControls.targetRecycledSharePct,
          material_reduction_pct: scenarioControls.materialReductionPct,
          transport_reduction_pct: scenarioControls.transportReductionPct,
        },
      }
    )
    .then((response) => response.data);

function ScenarioCard({
  title,
  description,
  sliderLabel,
  sliderValue,
  min,
  max,
  step,
  onSliderChange,
  result,
  unitName,
  helperText,
  resultLabel,
}: ScenarioCardProps) {
  const deltaColor = getDeltaColor(result?.delta);

  return (
    <Box
      sx={{
        height: '100%',
        p: 3,
        borderRadius: '20px',
        border: '1px solid #E9E3DD',
        background: 'linear-gradient(180deg, #FFFDF9 0%, #FFFFFF 100%)',
      }}
    >
      <Typography variant="h6" sx={{ color: '#281302', mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B5B4D', minHeight: 42 }}>
        {description}
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ color: '#281302', mb: 1 }}>
          {sliderLabel}: {sliderValue}%
        </Typography>
        <Slider
          value={sliderValue}
          min={min}
          max={max}
          step={step}
          valueLabelDisplay="auto"
          onChange={(_, value) => onSliderChange(value as number)}
          sx={{
            color: '#CC8720',
            '& .MuiSlider-thumb': {
              boxShadow: '0 0 0 8px rgba(204, 135, 32, 0.12)',
            },
          }}
        />
      </Box>
      {helperText && (
        <Typography variant="caption" sx={{ color: '#6B5B4D' }}>
          {helperText}
        </Typography>
      )}
      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: '16px',
          backgroundColor: '#F8F4EF',
        }}
      >
        <Typography variant="caption" sx={{ color: '#6B5B4D' }}>
          {resultLabel}
        </Typography>
        <Typography variant="h5" sx={{ color: '#281302' }}>
          {formatNumber(result?.scenario_total || 0)} {unitName}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: deltaColor, fontWeight: 600, mt: 1 }}
        >
          {`${result && result.delta <= 0 ? '' : '+'}${formatNumber(
            result?.delta || 0
          )} ${unitName} (${`${result && result.delta_pct <= 0 ? '' : '+'}${formatNumber(
            result?.delta_pct || 0
          )}%`})`}
        </Typography>
      </Box>
    </Box>
  );
}

function ComparisonMetricCell({
  result,
  unitName,
  isLoading,
  isError,
}: {
  result?: ScenarioImpact;
  unitName: string;
  isLoading?: boolean;
  isError?: boolean;
}) {
  const { t } = useTranslation('lca');

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 2,
          minHeight: 118,
          borderRadius: '18px',
          border: '1px solid #ECE4DA',
          backgroundColor: '#FFF',
        }}
      >
        <Typography variant="body2" sx={{ color: '#6B5B4D' }}>
          {t('loading')}
        </Typography>
      </Box>
    );
  }

  if (isError || !result) {
    return (
      <Box
        sx={{
          p: 2,
          minHeight: 118,
          borderRadius: '18px',
          border: '1px solid #F2D7D5',
          backgroundColor: '#FFF8F7',
        }}
      >
        <Typography variant="body2" sx={{ color: '#B3261E' }}>
          {t('scenarioExplorer.unavailable', {
            defaultValue: 'Unavailable',
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        minHeight: 118,
        borderRadius: '18px',
        border: '1px solid #ECE4DA',
        backgroundColor: '#FFF',
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ color: '#281302', fontWeight: 700, lineHeight: 1.3 }}
      >
        {formatNumber(result.scenario_total)} {unitName}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: getDeltaColor(result.delta), fontWeight: 700, mt: 0.75 }}
      >
        {`${result.delta <= 0 ? '' : '+'}${formatNumber(result.delta)} ${unitName}`}
      </Typography>
      <Typography variant="caption" sx={{ color: '#6B5B4D', display: 'block' }}>
        {t('scenarioExplorer.deltaVsBaseline', {
          defaultValue: '{{value}} vs baseline',
          value: `${result.delta_pct <= 0 ? '' : '+'}${formatNumber(
            result.delta_pct
          )}%`,
        })}
      </Typography>
    </Box>
  );
}

const ScenarioSimulationPanel: React.FC<ScenarioSimulationPanelProps> = ({
  productId,
  controls,
  onControlsChange,
  savedScenarios,
  onSavedScenariosChange,
}) => {
  const { selectedAreaOfImpact } = useAppSelector((state) => state.lcaDiagram);
  const { t } = useTranslation('lca');
  const [scenarioName, setScenarioName] = useState('');
  const getScenarioName = (count: number) =>
    t('scenarioExplorer.generatedName', {
      defaultValue: 'Scenario {{number}}',
      number: count + 1,
    });
  const formatScenarioSummaryItem = (
    key: 'recycled' | 'lightweighting' | 'transport',
    value: number
  ) => {
    const defaultValueMap = {
      recycled: '{{value}}% recycled',
      lightweighting: '{{value}}% lighter',
      transport: '{{value}}% transport cut',
    } as const;

    return t(`scenarioExplorer.summary.${key}`, {
      defaultValue: defaultValueMap[key],
      value,
    });
  };

  const deferredTargetRecycledSharePct = useDeferredValue(
    controls.targetRecycledSharePct
  );
  const deferredMaterialReductionPct = useDeferredValue(
    controls.materialReductionPct
  );
  const deferredTransportReductionPct = useDeferredValue(
    controls.transportReductionPct
  );

  const { data, isLoading, isError, error } = useQuery(
    [
      'lca-diagram',
      'analytics',
      'scenario-simulation',
      {
        productId,
        selectedAreaOfImpact,
        deferredTargetRecycledSharePct,
        deferredMaterialReductionPct,
        deferredTransportReductionPct,
      },
    ],
    () =>
      fetchScenarioSimulation(productId, selectedAreaOfImpact!.id, {
        targetRecycledSharePct: deferredTargetRecycledSharePct,
        materialReductionPct: deferredMaterialReductionPct,
        transportReductionPct: deferredTransportReductionPct,
      }),
    {
      enabled: !!selectedAreaOfImpact,
    }
  );

  const savedScenarioQueries = useQueries(
    savedScenarios.map((scenario) => ({
      queryKey: [
        'lca-diagram',
        'analytics',
        'scenario-simulation',
        'saved',
        {
          productId,
          areaId: selectedAreaOfImpact?.id,
          scenarioId: scenario.id,
          ...scenario.controls,
        },
      ],
      queryFn: () =>
        fetchScenarioSimulation(
          productId,
          selectedAreaOfImpact!.id,
          scenario.controls
        ),
      enabled: !!selectedAreaOfImpact,
    }))
  );

  if (!selectedAreaOfImpact) {
    return null;
  }

  const comparisonMetrics: ComparisonMetric[] = [
    {
      key: 'recycled_content',
      title: t('scenarioExplorer.recycled.title', {
        defaultValue: 'Recycled content',
      }),
      subtitle: t('scenarioExplorer.recycled.slider', {
        defaultValue: 'Target recycled share',
      }),
    },
    {
      key: 'lightweighting',
      title: t('scenarioExplorer.lightweighting.title', {
        defaultValue: 'Lightweighting',
      }),
      subtitle: t('scenarioExplorer.lightweighting.slider', {
        defaultValue: 'Material reduction',
      }),
    },
    {
      key: 'transport_optimization',
      title: t('scenarioExplorer.transport.title', {
        defaultValue: 'Transport optimization',
      }),
      subtitle: t('scenarioExplorer.transport.slider', {
        defaultValue: 'Transport reduction',
      }),
    },
  ];

  const canSaveMore = savedScenarios.length < MAX_SAVED_SCENARIOS;
  const currentScenarioSummary = [
    formatScenarioSummaryItem('recycled', controls.targetRecycledSharePct),
    formatScenarioSummaryItem('lightweighting', controls.materialReductionPct),
    formatScenarioSummaryItem('transport', controls.transportReductionPct),
  ];

  const handleSaveScenario = () => {
    if (!canSaveMore) {
      return;
    }

    const nextScenario: SavedScenario = {
      id: createScenarioId(),
      name: scenarioName.trim() || getScenarioName(savedScenarios.length),
      color:
        SCENARIO_COMPARE_COLORS[
          savedScenarios.length % SCENARIO_COMPARE_COLORS.length
        ],
      controls: { ...controls },
    };

    onSavedScenariosChange([...savedScenarios, nextScenario]);
    setScenarioName('');
  };

  const handleDeleteScenario = (scenarioId: string) => {
    onSavedScenariosChange(
      savedScenarios.filter((scenario) => scenario.id !== scenarioId)
    );
  };

  const handleLoadScenario = (scenario: SavedScenario) => {
    onControlsChange({ ...scenario.controls });
    setScenarioName(scenario.name);
  };

  const handleResetControls = () => {
    onControlsChange(DEFAULT_SCENARIO_CONTROLS);
    setScenarioName('');
  };

  const isDraftDefault = areControlsEqual(controls, DEFAULT_SCENARIO_CONTROLS);

  if (isLoading) {
    return (
      <StyledDiagramContainer>
        <StyledLabel>
          {t('scenarioExplorer.title', {
            defaultValue: 'Scenario explorer',
          })}{' '}
          [{selectedAreaOfImpact.unit_name}]
        </StyledLabel>
        <Typography>{t('loading')}</Typography>
      </StyledDiagramContainer>
    );
  }

  if (isError || !data) {
    const errorMessage =
      getLcaApiErrorMessage(error, t) ||
      t('scenarioExplorer.error', {
        defaultValue: 'Could not load scenario simulation.',
      });

    return (
      <StyledDiagramContainer>
        <StyledLabel>
          {t('scenarioExplorer.title', {
            defaultValue: 'Scenario explorer',
          })}{' '}
          [{selectedAreaOfImpact.unit_name}]
        </StyledLabel>
        <Typography color="error">{errorMessage}</Typography>
      </StyledDiagramContainer>
    );
  }

  return (
    <StyledDiagramContainer>
      <StyledLabel>
        {t('scenarioExplorer.title', {
          defaultValue: 'Scenario explorer',
        })}{' '}
        [{selectedAreaOfImpact.unit_name}]
      </StyledLabel>
      <Typography sx={{ color: '#6B5B4D', maxWidth: 900 }}>
        {t('scenarioExplorer.description', {
          defaultValue:
            'Estimated what-if scenarios based on the current stage footprint. These scenarios scale the most relevant life-cycle stages instead of recalculating the full product model.',
        })}
      </Typography>

      <Box
        sx={{
          mt: 3,
          mb: 3,
          p: 3,
          borderRadius: '20px',
          background:
            'linear-gradient(135deg, rgba(204, 135, 32, 0.14) 0%, rgba(184, 219, 229, 0.18) 100%)',
        }}
      >
        <Typography variant="caption" sx={{ color: '#6B5B4D' }}>
          {t('scenarioExplorer.baseline', {
            defaultValue: 'Baseline footprint',
          })}
        </Typography>
        <Typography variant="h4" sx={{ color: '#281302' }}>
          {formatNumber(data.baseline_total)} {selectedAreaOfImpact.unit_name}
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: '24px',
          border: '1px solid #ECE4DA',
          background:
            'linear-gradient(180deg, rgba(255,249,242,0.95) 0%, rgba(255,255,255,1) 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: '#281302', mb: 0.5 }}>
              {t('scenarioExplorer.compareTitle', {
                defaultValue: 'Scenario comparison workbench',
              })}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#6B5B4D', maxWidth: 760 }}
            >
              {t('scenarioExplorer.compareDescription', {
                defaultValue:
                  'Create up to 4 named scenarios, keep them side by side, and compare how each preset changes recycled content, material reduction, and transport optimization against the same baseline.',
              })}
            </Typography>
          </Box>
          <Chip
            label={t('scenarioExplorer.compareScope', {
              defaultValue: 'Comparison is lever-by-lever',
            })}
            sx={{
              backgroundColor: '#FFF3E3',
              color: '#7E4308',
              fontWeight: 700,
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 320px) auto' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label={t('scenarioExplorer.scenarioName', {
              defaultValue: 'Scenario name',
            })}
            value={scenarioName}
            onChange={(event) => setScenarioName(event.target.value)}
            placeholder={getScenarioName(savedScenarios.length)}
          />
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.25,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSaveScenario}
              disabled={!canSaveMore}
              sx={{
                backgroundColor: '#7E4308',
                '&:hover': {
                  backgroundColor: '#643406',
                },
              }}
            >
              {t('scenarioExplorer.saveScenario', {
                defaultValue: 'Save current scenario',
              })}
            </Button>
            <Button
              variant="outlined"
              onClick={handleResetControls}
              startIcon={<RestartAltOutlinedIcon />}
              sx={{
                borderColor: '#D3C5B6',
                color: '#281302',
              }}
            >
              {t('scenarioExplorer.resetDraft', {
                defaultValue: 'Reset draft',
              })}
            </Button>
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{ display: 'block', color: '#6B5B4D', mt: 1.5 }}
        >
          {canSaveMore
            ? t('scenarioExplorer.saveHint', {
                defaultValue:
                  'Save a snapshot of the current sliders, then load another preset to compare it against the draft.',
              })
            : t('scenarioExplorer.saveLimit', {
                defaultValue:
                  'Maximum of 4 saved scenarios to keep the comparison readable.',
              })}
        </Typography>

        <Box
          sx={{
            mt: 2.5,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              minWidth: 230,
              p: 1.5,
              borderRadius: '18px',
              border: '1px dashed #D7C5B2',
              backgroundColor: '#FFF',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#281302' }}>
              {t('scenarioExplorer.currentDraft', {
                defaultValue: 'Current draft',
              })}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#6B5B4D', display: 'block', mb: 1 }}
            >
              {isDraftDefault
                ? t('scenarioExplorer.currentDraftDefault', {
                    defaultValue: 'Using default slider values',
                  })
                : t('scenarioExplorer.currentDraftCustom', {
                    defaultValue: 'Unsaved slider setup',
                  })}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {currentScenarioSummary.map((item) => (
                <Chip key={item} label={item} size="small" />
              ))}
            </Box>
          </Box>

          {savedScenarios.map((scenario) => {
            const isLoaded = areControlsEqual(controls, scenario.controls);

            return (
              <Box
                key={scenario.id}
                sx={{
                  minWidth: 230,
                  p: 1.5,
                  borderRadius: '18px',
                  border: `1px solid ${scenario.color}`,
                  backgroundColor: '#FFF',
                  boxShadow: isLoaded
                    ? '0 10px 20px rgba(126, 67, 8, 0.08)'
                    : 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#281302' }}>
                      {scenario.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#6B5B4D', display: 'block', mb: 1 }}
                    >
                      {isLoaded
                        ? t('scenarioExplorer.loadedScenario', {
                            defaultValue: 'Loaded into sliders',
                          })
                        : t('scenarioExplorer.savedScenario', {
                            defaultValue: 'Saved preset',
                          })}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteScenario(scenario.id)}
                    aria-label={t('scenarioExplorer.deleteScenario', {
                      defaultValue: 'Delete {{name}}',
                      name: scenario.name,
                    })}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    mb: 1.25,
                  }}
                >
                  <Chip
                    label={formatScenarioSummaryItem(
                      'recycled',
                      scenario.controls.targetRecycledSharePct
                    )}
                    size="small"
                  />
                  <Chip
                    label={formatScenarioSummaryItem(
                      'lightweighting',
                      scenario.controls.materialReductionPct
                    )}
                    size="small"
                  />
                  <Chip
                    label={formatScenarioSummaryItem(
                      'transport',
                      scenario.controls.transportReductionPct
                    )}
                    size="small"
                  />
                </Box>
                <Button
                  size="small"
                  variant={isLoaded ? 'contained' : 'text'}
                  onClick={() => handleLoadScenario(scenario)}
                  sx={
                    isLoaded
                      ? {
                          backgroundColor: scenario.color,
                          '&:hover': {
                            backgroundColor: scenario.color,
                          },
                        }
                      : { color: scenario.color }
                  }
                >
                  {t('scenarioExplorer.loadScenario', {
                    defaultValue: 'Load into sliders',
                  })}
                </Button>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ScenarioCard
            title={t('scenarioExplorer.recycled.title', {
              defaultValue: 'Recycled content',
            })}
            description={t('scenarioExplorer.recycled.description', {
              defaultValue:
                'Scales raw materials extraction and processing (A1) based on the targeted average recycled share.',
            })}
            sliderLabel={t('scenarioExplorer.recycled.slider', {
              defaultValue: 'Target recycled share',
            })}
            sliderValue={controls.targetRecycledSharePct}
            min={0}
            max={100}
            step={1}
            onSliderChange={(value) =>
              onControlsChange({
                ...controls,
                targetRecycledSharePct: value,
              })
            }
            result={data.recycled_content}
            unitName={selectedAreaOfImpact.unit_name}
            helperText={t('scenarioExplorer.recycled.helper', {
              defaultValue: 'Current weighted recycled share: {{value}}%',
              value: formatNumber(data.current_recycled_share_pct),
            })}
            resultLabel={t('scenarioExplorer.resultLabel', {
              defaultValue: 'Scenario footprint',
            })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ScenarioCard
            title={t('scenarioExplorer.lightweighting.title', {
              defaultValue: 'Lightweighting',
            })}
            description={t('scenarioExplorer.lightweighting.description', {
              defaultValue:
                'Scales A1, A2, A3, A4 and C to estimate the effect of using less material and shipping less mass.',
            })}
            sliderLabel={t('scenarioExplorer.lightweighting.slider', {
              defaultValue: 'Material reduction',
            })}
            sliderValue={controls.materialReductionPct}
            min={0}
            max={50}
            step={1}
            onSliderChange={(value) =>
              onControlsChange({
                ...controls,
                materialReductionPct: value,
              })
            }
            result={data.lightweighting}
            unitName={selectedAreaOfImpact.unit_name}
            resultLabel={t('scenarioExplorer.resultLabel', {
              defaultValue: 'Scenario footprint',
            })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ScenarioCard
            title={t('scenarioExplorer.transport.title', {
              defaultValue: 'Transport optimization',
            })}
            description={t('scenarioExplorer.transport.description', {
              defaultValue:
                'Scales transport of raw materials (A2) and distribution/storage (A4) to estimate shorter routes or cleaner logistics.',
            })}
            sliderLabel={t('scenarioExplorer.transport.slider', {
              defaultValue: 'Transport reduction',
            })}
            sliderValue={controls.transportReductionPct}
            min={0}
            max={100}
            step={1}
            onSliderChange={(value) =>
              onControlsChange({
                ...controls,
                transportReductionPct: value,
              })
            }
            result={data.transport_optimization}
            unitName={selectedAreaOfImpact.unit_name}
            resultLabel={t('scenarioExplorer.resultLabel', {
              defaultValue: 'Scenario footprint',
            })}
          />
        </Grid>
      </Grid>

      {savedScenarios.length > 0 && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: '24px',
            border: '1px solid #ECE4DA',
            backgroundColor: '#FCFAF7',
          }}
        >
          <Typography variant="h6" sx={{ color: '#281302', mb: 0.5 }}>
            {t('scenarioExplorer.comparisonMatrix', {
              defaultValue: 'Saved scenario comparison',
            })}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B5B4D', mb: 2.5 }}>
            {t('scenarioExplorer.comparisonMatrixDescription', {
              defaultValue:
                'Compare the current draft and saved presets across the three Scenario Explorer levers. Each cell shows scenario footprint and delta versus the shared baseline.',
            })}
          </Typography>

          <Box sx={{ overflowX: 'auto' }}>
            <Box
              sx={{
                minWidth: 980,
                display: 'grid',
                gridTemplateColumns:
                  'minmax(250px, 1.2fr) repeat(3, minmax(210px, 1fr))',
                gap: 1.5,
                alignItems: 'stretch',
              }}
            >
              <Box />
              {comparisonMetrics.map((metric) => (
                <Box
                  key={metric.key}
                  sx={{
                    p: 2,
                    borderRadius: '18px',
                    backgroundColor: '#F4EEE7',
                    border: '1px solid #ECE4DA',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#281302' }}>
                    {metric.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B5B4D' }}>
                    {metric.subtitle}
                  </Typography>
                </Box>
              ))}

              <Box
                sx={{
                  p: 2,
                  borderRadius: '18px',
                  border: '1px dashed #D7C5B2',
                  backgroundColor: '#FFF',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#281302' }}>
                  {t('scenarioExplorer.currentDraft', {
                    defaultValue: 'Current draft',
                  })}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B5B4D' }}>
                  {t('scenarioExplorer.currentDraftMatrix', {
                    defaultValue:
                      'Live values from the sliders above. Use this row as the working comparison baseline.',
                  })}
                </Typography>
              </Box>
              {comparisonMetrics.map((metric) => (
                <ComparisonMetricCell
                  key={`draft-${metric.key}`}
                  result={data[metric.key]}
                  unitName={selectedAreaOfImpact.unit_name}
                />
              ))}

              {savedScenarios.map((scenario, index) => {
                const query = savedScenarioQueries[index];
                return (
                  <React.Fragment key={scenario.id}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '18px',
                        border: `1px solid ${scenario.color}`,
                        backgroundColor: '#FFF',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: '#281302' }}>
                        {scenario.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: '#6B5B4D', display: 'block', mb: 1 }}
                      >
                        {[
                          formatScenarioSummaryItem(
                            'recycled',
                            scenario.controls.targetRecycledSharePct
                          ),
                          formatScenarioSummaryItem(
                            'lightweighting',
                            scenario.controls.materialReductionPct
                          ),
                          formatScenarioSummaryItem(
                            'transport',
                            scenario.controls.transportReductionPct
                          ),
                        ].join(', ')}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => handleLoadScenario(scenario)}
                        sx={{ color: scenario.color, px: 0 }}
                      >
                        {t('scenarioExplorer.loadScenario', {
                          defaultValue: 'Load into sliders',
                        })}
                      </Button>
                    </Box>
                    {comparisonMetrics.map((metric) => (
                      <ComparisonMetricCell
                        key={`${scenario.id}-${metric.key}`}
                        result={query.data?.[metric.key]}
                        unitName={selectedAreaOfImpact.unit_name}
                        isLoading={query.isLoading}
                        isError={query.isError}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </StyledDiagramContainer>
  );
};

export default ScenarioSimulationPanel;
